import React, { useEffect, useMemo, useState } from "react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import Card from "../../components/Card";
import KpiTile, { KpiGrid } from "../../components/Dashboard/KpiTile";
import { getReferralStats } from "../../api/referrals.admin.service";
import { errorCodeOf, isOk, PLATFORM_LABEL, useErrorText, useFormat } from "./shared";
import styles from "./Referrals.module.sass";

const DAY = 86_400_000;
const RANGES = [
  { key: "30d", days: 30, granularity: "day" },
  { key: "90d", days: 90, granularity: "week" },
  { key: "12m", days: 365, granularity: "month" },
  { key: "all", days: null, granularity: "month" },
];

/**
 * Resumen básico de F1 (06 §2.1): canjes por programa, revisión, rechazos y días
 * regalados con el periodo anterior, serie, fuentes, plataformas y top de
 * códigos. Conversión, ingresos y comisiones llegan con F2/F3.
 */
const SummaryTab = ({ onOpenReview }) => {
  const { t } = useTranslation();
  const fmt = useFormat();
  const errorText = useErrorText();
  const [range, setRange] = useState("30d");
  const [metric, setMetric] = useState("claims");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const r = RANGES.find((x) => x.key === range);
    const to = new Date();
    const from = r.days ? new Date(to.getTime() - r.days * DAY) : new Date("2026-01-01");
    setData(null);
    getReferralStats({ from: from.toISOString(), to: to.toISOString(), granularity: r.granularity }).then((resp) =>
      isOk(resp) ? (setData(resp.data), setError(null)) : setError(errorCodeOf(resp))
    );
  }, [range]);

  const tiles = useMemo(() => {
    if (!data) return [];
    const k = data.kpis;
    const tile = (key, kpi, color, opts = {}) => ({
      key,
      value: fmt.num(kpi.value),
      delta: { now: kpi.value, before: kpi.previous, invert: opts.invert },
      color,
      spark: opts.spark,
    });
    return [
      tile("claims", k.claims, "#2A85FF", { spark: data.series.map((b) => b.claims) }),
      tile("claimsFriend", k.claimsFriend, "#83BF6E", { spark: data.series.map((b) => b.friend) }),
      tile("claimsPartner", k.claimsPartner, "#8E59FF", { spark: data.series.map((b) => b.partner) }),
      tile("review", k.review, "#FFB020", { invert: true }),
      tile("rejected", k.rejected, "#FF6A55", { invert: true }),
      tile("welcomeDays", k.welcomeDays, "#FF6AC2"),
      ...(k.revenueNetEurCents
        ? [
            {
              key: "revenue",
              value: fmt.eur(k.revenueNetEurCents.value),
              delta: { now: k.revenueNetEurCents.value, before: k.revenueNetEurCents.previous, fmt: "eur" },
              color: "#2ED0A0",
            },
            tile("payingInvitees", k.payingInvitees, "#2A85FF"),
            tile("inviterDays", k.inviterDays, "#8E59FF"),
          ]
        : []),
    ];
  }, [data, fmt]);

  if (error) return <div className={styles.empty}>{errorText(error)}</div>;
  if (!data) return <div className={styles.empty}>{t("referrals.common.loading")}</div>;

  // La serie es de canjes: las métricas sin serie propia enseñan el total.
  const seriesMetric = ["claimsFriend", "claimsPartner"].includes(metric) ? metric : "claims";
  const dataKey = { claims: "claims", claimsFriend: "friend", claimsPartner: "partner" }[seriesMetric];

  return (
    <>
      <div className={styles.rangeRow}>
        {data.kpis.reviewQueue > 0 ? (
          <button type="button" className={styles.alert} onClick={onOpenReview}>
            ⚠️ {t("referrals.summary.reviewQueue", { count: data.kpis.reviewQueue })} ›
          </button>
        ) : (
          <span />
        )}
        <div className={styles.ranges}>
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              className={cn(styles.rangeBtn, { [styles.rangeActive]: range === r.key })}
              onClick={() => setRange(r.key)}
            >
              {t(`referrals.summary.range_${r.key}`)}
            </button>
          ))}
        </div>
      </div>

      <KpiGrid>
        {tiles.map((tile) => (
          <KpiTile
            key={tile.key}
            label={t(`referrals.summary.kpi_${tile.key}`)}
            value={tile.value}
            sub={t("referrals.summary.vsPrevious")}
            delta={tile.delta}
            spark={tile.spark}
            color={tile.color}
            active={metric === tile.key}
            onClick={() => setMetric(tile.key)}
            locale={fmt.locale}
            formatMoney={fmt.eur}
          />
        ))}
      </KpiGrid>

      <Card className={styles.card} title={t(`referrals.summary.kpi_${seriesMetric}`)} classTitle="title-blue">
        {data.series.length === 0 ? (
          <div className={styles.empty}>{t("referrals.common.noData")}</div>
        ) : (
          <div className={styles.chart}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.series}>
                <CartesianGrid vertical={false} stroke="rgba(154,159,165,.2)" />
                <XAxis dataKey="bucket" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#9A9FA5" }} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#9A9FA5" }} />
                <Tooltip />
                <Bar dataKey={dataKey} fill="#2A85FF" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <div className={styles.grid3}>
        <Card className={styles.card} title={t("referrals.summary.bySource")} classTitle="title-green">
          {data.bySource.map((s) => (
            <div key={s.source} className={styles.kv}>
              <span>{t(`referrals.source.${s.source}`, { defaultValue: s.source })}</span>
              <strong>{fmt.num(s.count)}</strong>
            </div>
          ))}
          {data.bySource.length === 0 && <div className={styles.empty}>{t("referrals.common.noData")}</div>}
        </Card>
        <Card className={styles.card} title={t("referrals.summary.byPlatform")} classTitle="title-purple">
          {data.byPlatform.map((p) => (
            <div key={p.platform} className={styles.kv}>
              <span>{PLATFORM_LABEL[p.platform] ?? t("referrals.common.unknownPlatform")}</span>
              <strong>{fmt.num(p.count)}</strong>
            </div>
          ))}
          {data.byPlatform.length === 0 && <div className={styles.empty}>{t("referrals.common.noData")}</div>}
        </Card>
        <Card className={styles.card} title={t("referrals.summary.topCodes")} classTitle="title-yellow">
          {data.topCodes.map((c) => (
            <div key={`${c.code}-${c.programKey}`} className={styles.kv}>
              <span>
                <code>{c.code}</code> <span className={styles.muted}>{t(`referrals.program.${c.programKey}`)}</span>
              </span>
              <strong>{fmt.num(c.claims)}</strong>
            </div>
          ))}
          {data.topCodes.length === 0 && <div className={styles.empty}>{t("referrals.common.noData")}</div>}
        </Card>
      </div>
    </>
  );
};

export default SummaryTab;
